import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, Search } from "lucide-react";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { cn } from "../../utils";

import { searchTimezones, type EventTimezone } from "./timezones";

/**
 * A timezone dropdown that can be searched by city.
 *
 * A plain select jumps by the first letter of a label, and every label here
 * starts with "(GMT", so typing found nothing. Worse, a city that keeps
 * another city's clock is not on any label: somebody looking for San
 * Francisco had to know it is Los Angeles time. The search box matches the
 * label, the IANA name and `TIMEZONE_SEARCH_ALIASES`; see `searchTimezones`.
 *
 * The value is always the IANA name of one of `options`, never the text
 * typed, so nothing about what is stored changes.
 *
 * Keyboard: Enter, Space or ArrowDown on the trigger opens the list with the
 * search box focused. Arrow keys move through the matches, Enter picks one,
 * Escape closes without a change, and Tab closes it too, handing focus back
 * to the trigger so the next Tab carries on through the form. The box is an
 * ARIA combobox that owns the list, and the active option is announced
 * through `aria-activedescendant`.
 *
 * The trigger is named by the field's label and the current value together
 * (`labelId`), so a screen reader says "Timezone, (GMT+01:00) Berlin ..."
 * rather than the value alone. Radix sets `aria-haspopup="dialog"` and
 * `aria-expanded` on it, which is what the popover is.
 */
export function TimezonePicker({
  id,
  labelId,
  value,
  onChange,
  options,
  disabled,
  testId,
  placeholder = "Choose a timezone",
  className,
}: {
  id?: string;
  /** The id of the visible label, read out before the current value. */
  labelId?: string;
  value: string;
  onChange: (zone: string) => void;
  options: readonly EventTimezone[];
  disabled?: boolean;
  testId?: string;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpenState] = useState(false);
  // Read by onCloseAutoFocus, which Radix runs from a timeout after the
  // popover unmounts. Reopened by then, it must not pull focus back to the
  // trigger from the search box.
  const openRef = useRef(false);
  function setOpen(next: boolean) {
    openRef.current = next;
    setOpenState(next);
  }
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const valueId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;

  const matches = useMemo(
    () => searchTimezones(query, options),
    [query, options],
  );
  const selected = options.find((option) => option.zone === value);

  function openChange(next: boolean) {
    setOpen(next);
    if (next) {
      setQuery("");
      const index = options.findIndex((option) => option.zone === value);
      setActive(Math.max(index, 0));
      // The list mounts on this render; scroll the current zone into view
      // once it has.
      requestAnimationFrame(() =>
        document
          .getElementById(optionId(Math.max(index, 0)))
          ?.scrollIntoView({ block: "nearest" }),
      );
    }
  }

  // Focus the box once the popover has rendered, however it was opened. On
  // a desktop browser an ArrowDown on the trigger left focus behind on it
  // when this relied on the popover's own open-focus event alone.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  function choose(zone: string) {
    onChange(zone);
    setOpen(false);
  }

  function moveTo(index: number) {
    setActive(index);
    document
      .getElementById(optionId(index))
      ?.scrollIntoView({ block: "nearest" });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Tab") {
      // Nothing else in the popover takes focus, so Tab would otherwise leave
      // it open behind whatever field comes next. Closing returns focus to
      // the trigger (Radix's onCloseAutoFocus).
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (matches.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveTo(Math.min(active + 1, matches.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveTo(Math.max(active - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      moveTo(0);
    } else if (event.key === "End") {
      event.preventDefault();
      moveTo(matches.length - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const match = matches[active];
      if (match) choose(match.zone);
    }
  }

  return (
    <Popover.Root open={open} onOpenChange={openChange}>
      <Popover.Trigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          data-testid={testId}
          aria-labelledby={labelId ? `${labelId} ${valueId}` : undefined}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" && !open) {
              event.preventDefault();
              openChange(true);
            }
          }}
          className={cn(
            "flex h-10 max-sm:h-11 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm font-normal text-secondary-800 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-hb-blue/25 disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        >
          <span
            id={valueId}
            className={cn("line-clamp-1", !selected && "text-secondary-400")}
          >
            {selected?.label ?? placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className="z-50 flex w-(--radix-popover-trigger-width) min-w-72 max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-md border bg-popover text-secondary-800 shadow-md"
          style={{
            maxHeight:
              "min(24rem, var(--radix-popover-content-available-height))",
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            searchRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            if (openRef.current) event.preventDefault();
          }}
        >
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              aria-label="Search timezones by city or name"
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={
                matches.length > 0 ? optionId(active) : undefined
              }
              placeholder="Search a city, e.g. San Francisco"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
                if (listRef.current) listRef.current.scrollTop = 0;
              }}
              onKeyDown={onKeyDown}
              className="h-11 w-full bg-transparent text-base outline-hidden sm:text-sm placeholder:text-muted-foreground"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label="Timezones"
            className="min-h-0 flex-1 overflow-y-auto p-1"
          >
            {matches.map((option, index) => (
              <li
                key={option.zone}
                id={optionId(index)}
                role="option"
                aria-selected={option.zone === value}
                data-active={index === active || undefined}
                onMouseMove={() => setActive(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(option.zone)}
                className="flex cursor-pointer select-none items-start gap-2 rounded-sm p-3 text-sm data-[active]:bg-secondary-50"
              >
                <Check
                  className={cn(
                    "mt-0.5 h-4 w-4 shrink-0",
                    option.zone === value ? "opacity-100" : "opacity-0",
                  )}
                  aria-hidden
                />
                <span className="min-w-0">
                  {option.label}
                  {option.matchedAlias && (
                    <span className="block text-xs text-hb-navy-light">
                      Includes {option.matchedAlias}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {matches.length === 0 && (
            <p
              className="p-3 text-sm text-hb-navy-light"
              role="status"
              data-testid={testId ? `${testId}-no-match` : undefined}
            >
              No timezone matches “{query.trim()}”. Try the nearest large city.
            </p>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
